import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faMoon, faEye, faFilter, faTimes, faSearch, faSync, faBuilding, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import PackageTabs from "../../Components/AllTabs/PackageTabs";

const FALLBACK_IMAGE = "https://trawtelcdn.s3.eu-north-1.amazonaws.com/smarttravelcrm/package_images/default-package.jpg";

const DMCDealsList = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dmcIdFromUrl = searchParams.get("dmcId");

    const [lists, setLists] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [rawResponse, setRawResponse] = useState({});

    // --- Search & Pagination States ---
    const [searchText, setSearchText] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // --- NEW: DESTINATION TAGS STATE ---
    const [selectedDestination, setSelectedDestination] = useState("ALL");
    const destinationTags = ["ALL", "KASHMIR", "MALAYSIA", "DUBAI", "THAILAND", "SINGAPORE", "BALI", "VIETNAM", "KERALA"];

    const GetData = async (pNum = 1, sText = searchText, tag = selectedDestination) => {
    if (!user?.cid) return;
    setIsLoading(true);

    const idToPass = dmcIdFromUrl || "00000000-0000-0000-0000-000000000000";
    
    // Check for "ALL" tag
    const destParam = tag === "ALL" ? "" : tag;

    try {
        // Updated URL string to include the &destination= parameter
        const url = `${config.GETDMCPACKAGESBYCOMPANYID}?companyId=${idToPass}&search=${sText}&destination=${destParam}&pageNumber=${pNum}&pageSize=${pageSize}`;
        
        const res = await apiService.getAll(url);
        if (res) {
            setLists(res.data || []);
            setTotalRecords(res.totalRecords || 0);
            setTotalPages(res.totalPages || 0);
            setPageNumber(res.pageNumber || 1);
            setRawResponse(res);
        }
    } catch (error) {
        console.error("Error fetching packages:", error);
    } finally {
        setIsLoading(false);
    }
};

    // --- Handlers ---
    const handleSearch = () => {
        setPageNumber(1);
        GetData(1, searchText, selectedDestination);
    };

    const handleReset = () => {
        setSearchText("");
        setSelectedDestination("ALL");
        setPageNumber(1);
        if (dmcIdFromUrl) { navigate("/dmcpackagesdeals"); }
        GetData(1, "", "ALL"); 
    };

    const handleTagClick = (tag) => {
        setSelectedDestination(tag);
        setPageNumber(1);
        // When clicking a tag, we prioritize the tag filter
        GetData(1, searchText, tag);
    };

    const handlePageChange = (pNum) => {
        setPageNumber(pNum);
        GetData(pNum, searchText, selectedDestination);
    };

    useEffect(() => {
        if (!loading && user) {
            GetData(1, searchText, selectedDestination);
        }
    }, [user, loading, dmcIdFromUrl]);

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        let pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i} className={`page-item ${pageNumber === i ? 'active' : ''}`} onClick={() => handlePageChange(i)}>
                    <button className="page-link">{i}</button>
                </li>
            );
        }
        return (
            <ul className="pagination pagination-separated pagination-sm mb-0">
                <li className={`page-item ${rawResponse.previousPage ? "" : "disabled"}`} onClick={() => handlePageChange(1)}>
                    <button className="page-link">First</button>
                </li>
                {pages}
                <li className={`page-item ${rawResponse.nextPage ? "" : "disabled"}`} onClick={() => handlePageChange(totalPages)}>
                    <button className="page-link">Last</button>
                </li>
            </ul>
        );
    };

    if (loading) return <div className="p-5 text-center">Loading...</div>;

    return (
        <div id="layout-wrapper" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
            <Header User={user} activeSection="packages" />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="col-xl-12">
                            <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                <div className="p-3 border-bottom">
                                    <PackageTabs pageName="DMCPACKAGESDEALS" />
                                </div>

                                <div className="card-body">
                                    {/* SEARCH & FILTERS BOX */}
                                    <div className="bg-light p-4 rounded-3 mb-4 border">
                                        <div className="row g-2 align-items-center mb-3">
                                            <div className="col-md-6">
                                                <div className="search-box">
                                                    <input
                                                        type="text"
                                                        className="form-control border-0 shadow-sm"
                                                        placeholder="Search by Package Name, Destination, or DMC Name..."
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4 d-flex gap-2">
                                                <button className="btn btn-primary px-4 shadow-sm" onClick={handleSearch}>
                                                    <FontAwesomeIcon icon={faSearch} className="me-2" /> Search
                                                </button>
                                                <button className="btn btn-danger px-4 shadow-sm" onClick={handleReset}>
                                                    <FontAwesomeIcon icon={faSync} className="me-2" /> Reset
                                                </button>
                                            </div>
                                        </div>

                                        {/* --- DESTINATION TAGS UI --- */}
                                        <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top">
                                            <span className="text-muted fw-medium me-2 py-1 small">Quick Filters:</span>
                                            {destinationTags.map((tag) => (
                                                <button
                                                    key={tag}
                                                    className={`btn btn-sm rounded-pill px-3 fw-bold ${selectedDestination === tag ? "btn-primary shadow-sm" : "btn-soft-secondary text-dark border"}`}
                                                    onClick={() => handleTagClick(tag)}
                                                    style={{ fontSize: '11px' }}
                                                >
                                                    {tag}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* INFO ALERT FOR DMC FILTER */}
                                    {dmcIdFromUrl && (
                                        <div className="alert alert-soft-primary d-flex align-items-center justify-content-between mb-4 border-0">
                                            <div className="fs-14 fw-medium">
                                                <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" />
                                                Showing packages for a specific DMC Partner
                                            </div>
                                            <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={handleReset}>
                                                <FontAwesomeIcon icon={faTimes} className="me-1" /> View All DMCs
                                            </button>
                                        </div>
                                    )}

                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status"></div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="row">
                                                {lists.length > 0 ? (
                                                    lists.map((pkg) => {
                                                        const hasImage = pkg.media?.images && pkg.media.images.length > 0;
                                                        const displayImage = hasImage ? pkg.media.images[0].url : pkg.heroImage || FALLBACK_IMAGE;
                                                        const allDestinations = pkg.destinations?.length > 0 
                                                            ? pkg.destinations.map(d => d.countryName).join(", ") 
                                                            : "DMC Package";

                                                        return (
                                                            <div key={pkg.id} className="col-xl-3 col-md-6 mb-4">
                                                                <div className="card h-100 shadow-sm border-0 card-animate" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                                                                    <div style={{ height: '160px', position: 'relative', background: '#e9ecef' }}>
                                                                        <img
                                                                            src={displayImage}
                                                                            alt={pkg.title}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                            onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                                                                        />
                                                                        {pkg.contentType === 2 && (
                                                                            <div style={{ position: "absolute", top: "10px", left: "10px", background: "#ef4444", color: "#fff", padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: "bold", zIndex: 1 }}>
                                                                                <FontAwesomeIcon icon={faFilePdf} /> PDF FLYER
                                                                            </div>
                                                                        )}
                                                                        {pkg.contentType !== 2 && (
                                                                            <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>
                                                                                <FontAwesomeIcon icon={faMoon} /> {pkg.totalNights}N / {pkg.totalDays}D
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="card-body p-3">
                                                                        <div className="mb-2">
                                                                            <span className="bg-soft-secondary px-2 py-1 fs-11 w-100 text-truncate text-start border border-dashed" title={pkg.companyName}>
                                                                                <FontAwesomeIcon icon={faBuilding} className="me-1 text-info" />
                                                                                <span style={{ color: "#0d6efd", fontWeight: "600" }}>{pkg.companyName || "DMC Partner"}</span>
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-primary small fw-bold mb-1 text-truncate" title={allDestinations}>
                                                                            <FontAwesomeIcon icon={faMapMarkerAlt} /> {allDestinations}
                                                                        </div>
                                                                        <h5 className="card-title mb-3 text-truncate" title={pkg.title} style={{ fontSize: '15px', fontWeight: '800' }}>{pkg.title}</h5>
                                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                                            {pkg.contentType !== 2 ? (
                                                                                <div>
                                                                                    <div className="text-muted small" style={{ fontSize: '10px' }}>STARTING AT</div>
                                                                                    <div className="text-dark fw-bold" style={{ fontSize: '15px' }}>{pkg.pricing?.currency || "INR"} {pkg.pricing?.landOnly?.double?.toLocaleString() || "0"}</div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-muted small italic">Flyer Package</div>
                                                                            )}
                                                                            <span className={`badge ${pkg.status === 1 ? 'bg-soft-success text-success' : 'bg-soft-warning text-warning'}`}>
                                                                                {pkg.status === 1 ? 'Active' : 'Draft'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex gap-2 border-top pt-3">
                                                                            <Link to={`/dmcpackageview/${pkg.id}`} className="btn btn-sm btn-primary flex-fill">
                                                                                <FontAwesomeIcon icon={faEye} /> View Details
                                                                            </Link>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-center py-5 w-100">
                                                        <FontAwesomeIcon icon={faMoon} style={{ fontSize: '40px', opacity: 0.2 }} />
                                                        <h5 className="mt-3">No Packages Found</h5>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Pagination Footer */}
                                            <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                                                <div className="flex-shrink-0">
                                                    <div className="text-muted">
                                                        Showing <span className="fw-semibold">{lists.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                                                    </div>
                                                </div>
                                                {renderPagination()}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DMCDealsList;